// /settings/appearance — тема оформления + подсветка терминов при чтении.
//
// Язык уехал в отдельный раздел /settings/language (как в IEC Hero),
// SRS-напоминания — в /settings/notifications (там же, где остальные типы
// уведомлений). Сюда переехал бывший раздел «Чтение»: это тоже про то, как
// выглядит текст на экране.
//
// Мутации идут через PowerSync local UPDATE (rules/offline-sync.md §13, §18).
// См. docs/ui-spec/settings.appearance.{desktop,mobile}.md.

import { Switch, ToggleGroup, ToggleGroupItem } from "@studyhub/ui";
import { toast } from "@studyhub/ui/toast";
import {
	IconDeviceLaptop,
	IconHighlight,
	IconMoon,
	IconPalette,
	IconSun,
} from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SettingsPageContainer, SettingsPageHeader } from "@/components/settings/page-header";
import { SettingsRow, SettingsSection } from "@/components/settings/settings-shell";
import { useSession } from "@/hooks/use-session";
import { cn } from "@/lib/cn";
import { getPowerSync } from "@/lib/powersync/client";
import { usePowerSyncQuery } from "@/lib/powersync/use-query";
import { useThemeStore } from "@/stores/theme";

type ThemeChoice = "light" | "dark" | "system";

interface LocalReadingRow {
	glossary_highlight_enabled: number | null;
	glossary_highlight_course_only: number | null;
}

export const Route = createFileRoute("/settings/appearance")({
	component: AppearanceRoute,
});

/**
 * ThemeSegment — тактильный 3-сегментный выбор темы «Картотека».
 * Утопленный трек (e0) + приподнятый активный сегмент (e1) с пружиной.
 * Светлая / Тёмная / Системная.
 */
function ThemeSegment({
	value,
	onChange,
}: {
	value: ThemeChoice;
	onChange: (next: ThemeChoice) => void;
}) {
	const { t } = useTranslation("settings");
	const options: Array<{ key: ThemeChoice; icon: typeof IconSun; label: string }> = [
		{ key: "light", icon: IconSun, label: t("appearance.themeLight") },
		{ key: "dark", icon: IconMoon, label: t("appearance.themeDark") },
		{ key: "system", icon: IconDeviceLaptop, label: t("appearance.themeSystem") },
	];

	return (
		<ToggleGroup
			type="single"
			value={value}
			onValueChange={(v) => v && onChange(v as ThemeChoice)}
			aria-label={t("appearance.theme")}
			className="sh-inset-field grid grid-cols-3 gap-1.5 rounded-[var(--radius-md)] p-1.5"
		>
			{options.map((opt) => {
				const Icon = opt.icon;
				const active = value === opt.key;
				return (
					<ToggleGroupItem
						key={opt.key}
						value={opt.key}
						className={cn(
							"flex min-h-[64px] cursor-pointer flex-col items-center justify-center gap-1.5 rounded-[var(--radius-sm)] border border-transparent text-xs font-medium text-muted-foreground",
							// Без пружины на цвете/фоне: overshoot-spring на background-color даёт
							// вспышку, которая «гаснет» (мерцающий hover). Пружина — только для transform.
							"transition-[background-color,box-shadow,color] duration-fast ease-out",
							// Стабильный hover для неактивных — перебивает hover:bg-muted из toggleVariants.
							"hover:bg-[var(--surface-2)] hover:text-foreground active:translate-y-px",
							// Активный сегмент — приподнятая белая плашка. data-[state=on]:-префиксы
							// обязательны, иначе twMerge НЕ перебьёт data-[state=on]:bg-well / :[box-shadow:e0]
							// из cva (разные группы → побеждает порядок CSS = well/e0). См. toggle cva.
							"data-[state=on]:bg-card data-[state=on]:font-semibold data-[state=on]:text-foreground",
							"data-[state=on]:[box-shadow:var(--sh-e1)] data-[state=on]:hover:bg-card",
						)}
					>
						<Icon size={20} stroke={1.75} className={active ? "text-primary" : "text-current"} />
						{opt.label}
					</ToggleGroupItem>
				);
			})}
		</ToggleGroup>
	);
}

function AppearanceRoute() {
	const { t } = useTranslation("settings");
	const theme = useThemeStore((s) => s.theme);
	const resolvedTheme = useThemeStore((s) => s.resolvedTheme);
	const setTheme = useThemeStore((s) => s.setTheme);

	const { user: me } = useSession();
	const userId = me?.id ?? "";

	function persistTheme(next: ThemeChoice) {
		setTheme(next);
		if (!userId) return;
		void getPowerSync()
			.execute(`UPDATE users SET theme = ?, updated_at = ? WHERE id = ?`, [
				next,
				new Date().toISOString(),
				userId,
			])
			.catch(() => toast.error(t("appearance.saveError")));
	}

	const systemPrefersDark =
		typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches;

	return (
		<SettingsPageContainer>
			<SettingsPageHeader
				title={t("appearance.title")}
				description={t("appearance.subtitle")}
				icon={<IconPalette stroke={1.75} />}
			/>

			<SettingsSection title={t("appearance.theme")} icon={<IconPalette stroke={1.75} />}>
				<div className="space-y-3 p-4">
					<ThemeSegment value={theme} onChange={persistTheme} />
					<p className="text-xs text-muted-foreground">
						{theme === "system"
							? t("appearance.themeSystemStatus", {
									resolved:
										resolvedTheme === "dark"
											? t("appearance.themeDark")
											: t("appearance.themeLight"),
								})
							: t("appearance.themeStatus", {
									resolved:
										resolvedTheme === "dark"
											? t("appearance.themeDark")
											: t("appearance.themeLight"),
									system: systemPrefersDark
										? t("appearance.themeDark")
										: t("appearance.themeLight"),
								})}
					</p>
				</div>
			</SettingsSection>

			<ReadingSection userId={userId} />
		</SettingsPageContainer>
	);
}

/**
 * Слой глоссария поверх текста (LEARNING-SYSTEM §8.4) — бывший /settings/reading.
 *
 * Тумблеры пишутся в `users` через PowerSync (поля в whitelist users-write-
 * handler) и РЕАЛЬНО влияют: их читает GlossaryDictionaryProvider
 * (hooks/use-glossary-terms.ts), от которого зависит вся подсветка.
 */
function ReadingSection({ userId }: { userId: string }) {
	const { t } = useTranslation("settings");

	const query = usePowerSyncQuery<LocalReadingRow>(
		`SELECT glossary_highlight_enabled, glossary_highlight_course_only FROM users WHERE id = ? LIMIT 1`,
		userId ? [userId] : [],
	);
	const row = query.data?.[0];

	// Колонка ещё не приехала (null) → серверные дефолты: подсветка включена,
	// «только курс» — выключен.
	const [highlight, setHighlight] = useState(true);
	const [courseOnly, setCourseOnly] = useState(false);
	const hydrated = useRef(false);

	useEffect(() => {
		if (!row || hydrated.current) return;
		hydrated.current = true;
		setHighlight(row.glossary_highlight_enabled !== 0);
		setCourseOnly(row.glossary_highlight_course_only === 1);
	}, [row]);

	function persist(
		field: "glossary_highlight_enabled" | "glossary_highlight_course_only",
		next: boolean,
	) {
		if (!userId) return;
		void getPowerSync()
			.execute(`UPDATE users SET ${field} = ?, updated_at = ? WHERE id = ?`, [
				next ? 1 : 0,
				new Date().toISOString(),
				userId,
			])
			.catch(() => {
				// Откат — как в profile-form: сохранение мгновенное, ошибка громкая.
				if (field === "glossary_highlight_enabled") setHighlight(!next);
				else setCourseOnly(!next);
				toast.error(t("reading.saveError"));
			});
	}

	return (
		<SettingsSection
			title={t("reading.title")}
			description={t("reading.subtitle")}
			icon={<IconHighlight stroke={1.75} />}
		>
			<SettingsRow
				label={t("reading.glossaryHighlight.title")}
				description={t("reading.glossaryHighlight.description")}
			>
				<Switch
					checked={highlight}
					onCheckedChange={(v) => {
						setHighlight(v);
						persist("glossary_highlight_enabled", v);
					}}
				/>
			</SettingsRow>
			<SettingsRow
				label={t("reading.glossaryCourseOnly.title")}
				description={t("reading.glossaryCourseOnly.description")}
				className={highlight ? undefined : "pointer-events-none opacity-50"}
			>
				<Switch
					checked={courseOnly}
					disabled={!highlight}
					onCheckedChange={(v) => {
						setCourseOnly(v);
						persist("glossary_highlight_course_only", v);
					}}
				/>
			</SettingsRow>
		</SettingsSection>
	);
}
